import { Modal, Button } from '@/shared/components/ui';
import { useState } from 'react';
import styles from './ModalsPage.module.css';

export const ModalsPage = () => {
    const [showBasic, setShowBasic] = useState(false);
    const [showWithTitle, setShowWithTitle] = useState(false);
    const [showSmall, setShowSmall] = useState(false);
    const [showLarge, setShowLarge] = useState(false);
    const [showXL, setShowXL] = useState(false);

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h1>Modals</h1>
                <p className={styles.description}>
                    Componente Modal para mostrar contenido en una ventana superpuesta.
                    Soporta diferentes tamaños, títulos y cierre con overlay o ESC.
                </p>
            </div>

            {/* Modal Básico */}
            <section className={styles.section}>
                <h2>Modal Básico</h2>
                <p>Modal simple sin título.</p>

                <div className={styles.example}>
                    <Button onClick={() => setShowBasic(true)}>Abrir Modal Básico</Button>

                    <Modal isOpen={showBasic} onClose={() => setShowBasic(false)}>
                        <p>Este es el contenido del modal. Puedes agregar cualquier elemento aquí.</p>
                        <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
                            <Button variant="primary" onClick={() => setShowBasic(false)}>Aceptar</Button>
                            <Button variant="secondary" onClick={() => setShowBasic(false)}>Cancelar</Button>
                        </div>
                    </Modal>

                    <code className={styles.code}>
                        {`const [isOpen, setIsOpen] = useState(false);

<Button onClick={() => setIsOpen(true)}>
  Abrir Modal
</Button>

<Modal isOpen={isOpen} onClose={() => setIsOpen(false)}>
  <p>Contenido del modal...</p>
</Modal>`}
                    </code>
                </div>
            </section>

            {/* Modal con Título */}
            <section className={styles.section}>
                <h2>Modal con Título</h2>
                <p>Modal con header que incluye título y botón de cerrar.</p>

                <div className={styles.example}>
                    <Button onClick={() => setShowWithTitle(true)}>Abrir Modal con Título</Button>

                    <Modal
                        isOpen={showWithTitle}
                        onClose={() => setShowWithTitle(false)}
                        title="Confirmación"
                    >
                        <p>¿Estás seguro de que deseas continuar con esta acción?</p>
                        <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                            Esta acción no puede deshacerse.
                        </p>
                        <div style={{ marginTop: '1.5rem', display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                            <Button variant="secondary" onClick={() => setShowWithTitle(false)}>Cancelar</Button>
                            <Button variant="danger" onClick={() => setShowWithTitle(false)}>Confirmar</Button>
                        </div>
                    </Modal>

                    <code className={styles.code}>
                        {`<Modal 
  isOpen={isOpen} 
  onClose={() => setIsOpen(false)}
  title="Confirmación"
>
  <p>¿Estás seguro...?</p>
  <Button variant="danger">Confirmar</Button>
</Modal>`}
                    </code>
                </div>
            </section>

            {/* Tamaños */}
            <section className={styles.section}>
                <h2>Tamaños</h2>
                <p>Modal disponible en 4 tamaños: Small, Medium, Large y Extra Large.</p>

                <div className={styles.showcase}>
                    <div className={styles.example}>
                        <Button size="sm" onClick={() => setShowSmall(true)}>Small (400px)</Button>
                        <Modal
                            isOpen={showSmall}
                            onClose={() => setShowSmall(false)}
                            title="Modal Small"
                            size="sm"
                        >
                            <p>Este es un modal pequeño, ideal para confirmaciones simples.</p>
                            <Button onClick={() => setShowSmall(false)}>Cerrar</Button>
                        </Modal>
                    </div>

                    <div className={styles.example}>
                        <Button size="sm" onClick={() => setShowWithTitle(true)}>Medium (600px)</Button>
                        <code className={styles.code}>{`size="md" // Default`}</code>
                    </div>

                    <div className={styles.example}>
                        <Button size="sm" onClick={() => setShowLarge(true)}>Large (800px)</Button>
                        <Modal
                            isOpen={showLarge}
                            onClose={() => setShowLarge(false)}
                            title="Modal Large"
                            size="lg"
                        >
                            <p>Modal grande para contenido más extenso como formularios complejos.</p>
                            <div style={{ marginTop: '1rem' }}>
                                <input type="text" placeholder="Campo 1" style={{ width: '100%', padding: '0.5rem', marginBottom: '0.5rem' }} />
                                <input type="text" placeholder="Campo 2" style={{ width: '100%', padding: '0.5rem', marginBottom: '0.5rem' }} />
                                <input type="text" placeholder="Campo 3" style={{ width: '100%', padding: '0.5rem' }} />
                            </div>
                            <div style={{ marginTop: '1rem' }}>
                                <Button onClick={() => setShowLarge(false)}>Cerrar</Button>
                            </div>
                        </Modal>
                    </div>

                    <div className={styles.example}>
                        <Button size="sm" onClick={() => setShowXL(true)}>Extra Large (1200px)</Button>
                        <Modal
                            isOpen={showXL}
                            onClose={() => setShowXL(false)}
                            title="Modal Extra Large"
                            size="xl"
                        >
                            <p>Modal extra grande para dashboards o contenido muy extenso.</p>
                            <p>Puede contener tablas, gráficos o múltiples secciones de contenido.</p>
                            <Button onClick={() => setShowXL(false)}>Cerrar</Button>
                        </Modal>
                    </div>
                </div>

                <code className={styles.code}>
                    {`<Modal size="sm">...</Modal>  // 400px
<Modal size="md">...</Modal>  // 600px (default)
<Modal size="lg">...</Modal>  // 800px
<Modal size="xl">...</Modal>  // 1200px`}
                </code>
            </section>

            {/* Ejemplos de Uso */}
            <section className={styles.section}>
                <h2>Ejemplos de Uso</h2>

                <div className={styles.exampleGroup}>
                    <h3>Modal de Confirmación</h3>
                    <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }}>
                        Usa modales para acciones destructivas que requieren confirmación.
                    </p>
                    <code className={styles.code}>
                        {`<Modal 
  isOpen={showDelete} 
  onClose={() => setShowDelete(false)}
  title="Eliminar Usuario"
  size="sm"
>
  <p>¿Estás seguro de eliminar a {usuario.nombre}?</p>
  <p>Esta acción no puede deshacerse.</p>
  
  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
    <Button variant="secondary" onClick={() => setShowDelete(false)}>
      Cancelar
    </Button>
    <Button variant="danger" onClick={handleDelete}>
      Eliminar
    </Button>
  </div>
</Modal>`}
                    </code>
                </div>

                <div className={styles.exampleGroup}>
                    <h3>Modal con Formulario</h3>
                    <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }}>
                        Perfecto para formularios de creación o edición.
                    </p>
                    <code className={styles.code}>
                        {`<Modal 
  isOpen={showForm} 
  onClose={() => setShowForm(false)}
  title="Agregar Usuario"
  size="md"
>
  <form onSubmit={handleSubmit}>
    <Input label="Nombre" required />
    <Input label="Email" type="email" required />
    <Input label="Rol" />
    
    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.5rem' }}>
      <Button type="submit" variant="primary">Guardar</Button>
      <Button type="button" variant="secondary" onClick={() => setShowForm(false)}>
        Cancelar
      </Button>
    </div>
  </form>
</Modal>`}
                    </code>
                </div>
            </section>

            {/* Comportamiento */}
            <section className={styles.section}>
                <h2>Comportamiento</h2>
                <div className={styles.infoBox}>
                    <h4>✅ El modal se cierra cuando:</h4>
                    <ul>
                        <li>Se hace click en el overlay (fondo oscuro)</li>
                        <li>Se hace click en el botón X del header (si tiene título)</li>
                        <li>Se llama a la función <code>onClose</code></li>
                    </ul>

                    <h4 style={{ marginTop: '1rem' }}>🎯 Características:</h4>
                    <ul>
                        <li>Click en el contenido del modal NO lo cierra</li>
                        <li>Scroll automático si el contenido es muy largo</li>
                        <li>Responsive: se adapta en mobile</li>
                        <li>Z-index alto para aparecer sobre todo</li>
                    </ul>
                </div>
            </section>

            {/* Props */}
            <section className={styles.section}>
                <h2>Props</h2>
                <div className={styles.propsTable}>
                    <table>
                        <thead>
                            <tr>
                                <th>Prop</th>
                                <th>Tipo</th>
                                <th>Default</th>
                                <th>Descripción</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><code>isOpen</code></td>
                                <td><code>boolean</code></td>
                                <td>-</td>
                                <td>Si el modal está visible (requerido)</td>
                            </tr>
                            <tr>
                                <td><code>onClose</code></td>
                                <td><code>() =&gt; void</code></td>
                                <td>-</td>
                                <td>Función para cerrar el modal (requerido)</td>
                            </tr>
                            <tr>
                                <td><code>title</code></td>
                                <td><code>string</code></td>
                                <td>-</td>
                                <td>Título del modal (opcional)</td>
                            </tr>
                            <tr>
                                <td><code>size</code></td>
                                <td><code>'sm' | 'md' | 'lg' | 'xl'</code></td>
                                <td><code>'md'</code></td>
                                <td>Tamaño del modal</td>
                            </tr>
                            <tr>
                                <td><code>children</code></td>
                                <td><code>ReactNode</code></td>
                                <td>-</td>
                                <td>Contenido del modal (requerido)</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </section>

            {/* Import */}
            <section className={styles.section}>
                <h2>Importación</h2>
                <code className={styles.code}>
                    {`import { Modal } from '@/shared/components/ui';`}
                </code>
            </section>
        </div>
    );
};
